/* @flow */
/* eslint-env es6,browser */

import {
  isFunction,
  isObject,
  isString,
  isUndefined,
  isDOMElement
} from './coreutils';
import { logError } from './logger';

/**
 * @description Browser Utility from EOW
 * @module utils/Browserutil
 * @requires utils/coreutils
 * @requires utils/logger
 */

/**
 * @class
 */
class _BrowserUtil {
  _ua: string;

  _config: Array<browserConfig>;

  _cache: browserConfig;

  _uaReal: string;

  constructor(ua?: string = navigator.userAgent) {
    this._uaReal = ua;
    this._ua = ua.toLowerCase();
    this._config = [
      {
        search: 'edge',
        name: 'Edge',
        sname: 'edge',
        versionPrefix: 'edge',
        version: ''
      },
      {
        search: 'opera',
        name: 'Opera',
        sname: 'opera',
        versionPrefix: 'opera',
        version: ''
      },
      {
        search: 'chrome',
        name: 'Chrome',
        sname: 'chrome',
        versionPrefix: 'chrome',
        version: ''
      },
      {
        search: 'firefox',
        name: 'Firefox',
        sname: 'ff',
        versionPrefix: 'firefox',
        version: ''
      },
      {
        search: 'safari',
        name: 'Safari',
        sname: 'safari',
        versionPrefix: 'version',
        version: ''
      },
      {
        search: 'msie',
        name: 'Internet Explorer',
        sname: 'ie',
        versionPrefix: 'msie',
        version: ''
      },
      {
        search: 'trident',
        name: 'Internet Explorer',
        sname: 'ie',
        versionPrefix: 'rv',
        version: ''
      }
    ];
    this._cache = this._getInfo();
  }

  _getInfo(): browserConfig {
    const info = this._getName();
    info.version = this._getVersion(info);
    return info;
  }

  _getName(): browserConfig {
    // set default value
    const defRet = {
      search: 'unknown',
      name: 'Unknown',
      sname: 'others',
      versionPrefix: '',
      version: ''
    };
    const index = this._config.findIndex(obj => this._ua.includes(obj.search));
    return index !== -1 ? this._config[index] : defRet;
  }

  _getVersion(info: browserConfig): string {
    let offset =
      this._ua.indexOf(info.versionPrefix) + info.versionPrefix.length + 1; // +1 is for '/' or space or ':' after prefix
    let ret = '';
    let ch = this._ua.charAt(offset);
    // eslint-disable-next-line no-loops/no-loops
    while (ch && ch !== ' ' && (!Number.isNaN(Number(ch)) || ch === '.')) {
      ret += ch;
      offset += 1;
      ch = this._ua.charAt(offset);
    }
    return ret
      .split('.')
      .splice(0, 2)
      .join('.');
  }

  /**
   * @description This function is used to get the name of the user's browser
   * @returns {string} - [Chrome, Safari, Internet Explorer, Opera, Firefox] or Unknown
   */
  getName(): string {
    return this._cache.name;
  }

  /**
   * @description short name of the browser
   * @returns {string} - [ff, chrome, safari, ie, opera]
   */
  getShortName(): string {
    return this._cache.sname;
  }

  /**
   * @description get browser version
   * @returns {string} - browser version
   */
  getVersion(): string {
    return this._cache.version;
  }

  /**
   * @description get browser major version
   * @returns {number} - Version Number
   */
  getMajorVersion(): number {
    return parseInt(this._cache.version, 10);
  }

  /**
   * @description check browser version is ie 10 and below
   * @returns {boolean} - IE<9 or not
   */
  isOldIE() {
    return this.getShortName() === 'ie' && this.getMajorVersion() <= 10;
  }

  /**
   * Returns useragent from navigator API
   *
   * @returns {string} - useragent
   */
  getUserAgent() {
    return this._uaReal;
  }
}

// export singleton object as default for this module
const BrowserUtil = new _BrowserUtil();
export { BrowserUtil, _BrowserUtil };

// flowlint unclear-type:off
/**
 * parse JSON ad response, take care of removing OAS tags
 * Need to check this during openx migration!!
 * https://github.com/rollup/rollup/wiki/Troubleshooting#avoiding-eval
 *
 * @param {window} [w] - window object
 * @param {string} [data] - data to be parsed
 * @returns {object} - Parsed JSON data
 */
export function parseJSON(w?: window = window, data?: string = ''): any {
  // flowlint unclear-type:error
  if (!isString(data) || !data.length) {
    return {};
  }
  /* jshint evil: true */
  const b = data.indexOf('{');
  const e = data.lastIndexOf('}');
  let s = '';
  if (b !== -1 && e !== -1 && e > b) {
    s = data.slice(b, e + 1);
  }
  if (s.length) {
    try {
      if (typeof w.JSON === 'object' && isFunction(w.JSON.parse)) {
        return w.JSON.parse(s);
      }
      if (isFunction(w.jQuery) && isFunction(w.jQuery.parseJSON)) {
        return w.jQuery.parseJSON(s);
      }
      const eval2 = w.eval;
      return eval2(`(${s})`);
    } catch (error) {
      /* continue regardless of error */
    }
  }
  return {};
}

/**
 * load a JS file asynchronously.
 * [c]2014 @scottjehl, Filament Group, Inc.
 * (Based on http://goo.gl/REQGQ by Paul Irish).
 * Licensed MIT
 * https://github.com/filamentgroup/loadJS/blob/master/loadJS.js
 *
 * @param {string} src - js file url
 * @param {Document} d - document object
 * @param {boolean} [bAsync=false] - async flag
 * @param {string} id - script tag id
 * @returns {HTMLScriptElement} - Newly created script DOM node
 */
export function loadJS(
  src: string,
  d: Document,
  bAsync?: boolean,
  id?: string
): HTMLScriptElement | null {
  bAsync = bAsync || false;
  src = src || '';
  id = id || '';
  if (!src.length) {
    return null;
  }
  const script = d.createElement('script');
  script.src = src;
  if (id.length) {
    script.id = id;
  }
  if (bAsync) {
    script.async = true;
  }
  const [ref] = d.getElementsByTagName('script');
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(script, ref);
  } else {
    try {
      if (d.body) {
        d.body.appendChild(script);
      }
    } catch (error) {
      /**/
    }
  }
  return script;
}

/**
 * @global
 * @typedef loadJSWithTimeoutParams
 * @type {object}
 * @property {object} [content=window]
 * @property {string} [function]
 * @property {string} [object]
 */

/**
 * Loads js file asynchronously
 *
 * @param {string} src - js file url
 * @param {Document} d - document object
 * @param {boolean} bAsync - async flag
 * @param {number} timeout - timeout for loading
 * @param {loadJSWithTimeoutParams} [o] - object to check for context/function
 * @returns {Promise<*>} - Promise for loading JS with timeout
 */
export function loadJSWithTimeout(
  src: string,
  d: Document,
  bAsync: boolean,
  timeout: number,
  o?: { context?: window, func?: string, object?: string }
): Promise<void> {
  const script = loadJS(src, d, bAsync);
  let done = false;
  let loadTimer: ?TimeoutID = null;
  o = o || {};
  o.context = o.context || window;
  o.func = o.func || '';
  o.object = o.object || '';

  if (!script) {
    return Promise.reject(`Script loading failed ${src}`);
  }
  // returns promise
  // prettier-ignore
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line jsdoc/require-jsdoc
    function loadHandler() {
      const err = `${src} script loading failed ${loadTimer ? ': timeout' : ''}`;
      // $FlowFixMe
      if (o.func.length || o.object.length) {
        // $FlowFixMe
        if ((!o.func.length || isFunction(o.context[o.func])) &&
          // $FlowFixMe
          (!o.object.length || isObject(o.context[o.object]))
        ) {
          return resolve();
        }
        logError(`${err} :context check failed`);
        return reject(err);
      }
      if (loadTimer) {
        logError(err);
        return reject(err);
      }
      return resolve();
    }

    if (!script) {
      reject(`Script loading failed ${src}`);
      return;
    }
    // load AOL script here
    loadTimer = setTimeout(loadHandler, timeout);
    // Attach handlers for all browsers
    /* eslint-disable unicorn/prefer-add-event-listener */
    // eslint-disable-next-line func-names, no-multi-assign
    script.onload = script.onreadystatechange = function () {
      if (
        !done &&
        (!this.readyState ||
          this.readyState === 'loaded' ||
          this.readyState === 'complete')
      ) {
        done = true;
        if (loadTimer) {
          clearTimeout(loadTimer);
          loadTimer = null;
        }
        loadHandler();
        if (!script) {
          return;
        }
        // reset handlers
        script.onload = null;
        script.onreadystatechange = null;
      }
    };
    /* eslint-enable unicorn/prefer-add-event-listener */
  });
}

/**
 * load a CSS file asynchronously.
 * [c]2014 @scottjehl, Filament Group, Inc.
 * Licensed MIT
 * https://github.com/filamentgroup/loadCSS/blob/master/loadCSS.js
 *
 * @param {string} href - link src url
 * @param {Document} d - document object
 * @param {Element} [before] - element before which link tag needs to be inserted
 * @param {string} [media=all] - media type
 * @returns {HTMLLinkElement} - Newly created link tag for loading CSS
 */
export function loadCSS(
  href: string,
  d: Document,
  before?: Element,
  media?: string = 'all'
): HTMLLinkElement {
  // Arguments explained:
  // `href` is the URL for your CSS file.
  // `before` optionally defines the element we'll use as a reference for injecting our <link>
  // By default, `before` uses the first <script> element in the page.
  // However, since the order in which stylesheets are referenced matters, you might need a more specific location in your document.
  // If so, pass a different reference element to the `before` argument and it'll insert before that instead
  // note: `insertBefore` is used instead of `appendChild`, for safety re: http://www.paulirish.com/2011/surefire-dom-element-insertion/
  const ss: HTMLLinkElement = d.createElement('link');
  const ref: Element = before || d.getElementsByTagName('script')[0];
  ss.rel = 'stylesheet';
  ss.href = href;
  // temporarily, set media to something non-matching to ensure it'll fetch without blocking render
  ss.media = 'only x';
  // inject link
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(ss, ref);
  }
  // set media back to `all` so that the styleshet applies once it loads
  setTimeout(() => {
    ss.media = media;
  });
  return ss;
}

/**
 * Preload given resource
 *
 * @param {string} href - link src url
 * @param {Document} d - document object
 * @param {string} [as] -  resource type (script/image/style/document)
 * @param {string} [type] - media type
 * @param {boolean} [crossorigin=false] - how to handle cross-origin request
 * @param {boolean} [before] - element before which link tag needs to be inserted
 * @returns {HTMLLinkElement} - Newly created link tag for preload
 */
export function preload(
  href: string,
  d: Document,
  as?: string = '',
  type?: string = '',
  crossorigin?: boolean = false,
  before?: Element
): HTMLLinkElement {
  const ref = before || d.getElementsByTagName('script')[0];
  const link = d.createElement('link');
  link.href = href;
  link.rel = 'preload';
  if (as.length) {
    link.as = as;
  }
  if (type.length) {
    link.type = type;
  }
  if (crossorigin) {
    link.crossOrigin = 'use-credentials';
  }
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(link, ref);
  }
  return link;
}

/**
 * Browser exclusion list support
 * We can exclude based on browser name, version, page name
 * Format: '<name>|<version>|<pagename>,...'
 * '*' to exlude all
 *
 * @example '*|*|write' - Will exclude compose page in all browser
 * @param {string} s - Exclusion list as string
 * @param {_BrowserUtil} [bu] - Browser Util object
 * @returns {Array<string>} - Get browser exclusion list as Array
 */
export function getBrowserExcludeList(
  s: string,
  bu: _BrowserUtil = BrowserUtil
): Array<string> {
  const bName = bu.getShortName();
  const bVer = bu.getMajorVersion();
  const excludeList = (s || '').toLowerCase().split(',');
  let i = 0;
  let tmp = '';
  let arr = [];
  const ret = [];
  // eslint-disable-next-line no-loops/no-loops
  for (; i < excludeList.length; i += 1) {
    tmp = excludeList[i];
    arr = tmp.split('|');
    if (arr.length === 3) {
      if (tmp.includes('*')) {
        arr[0] = arr[0] === '*' ? bName : arr[0];
        arr[1] = arr[1] === '*' ? String(bVer) : arr[1];
      }
      tmp = arr.join('|');
      ret.push(tmp.toLowerCase());
    }
  }
  return ret;
}

/**
 * Check if current page/browser combo is in exclude list
 *
 * @param {string} pname - pagename
 * @param {Array<string>} exclList - exclude list formulated using getBrowserExcludeList
 * @param {_BrowserUtil} [bu] - Browser Util object
 * @returns {boolean} - Browser matches with exclusion list (name/version)
 */
export function isBrowserExcluded(
  pname: string,
  exclList: Array<string>,
  bu: _BrowserUtil = BrowserUtil
): boolean {
  pname = pname || '';
  exclList = exclList || [];
  if (!pname.length || !exclList.length) {
    return false;
  }
  const prefix = `${bu.getShortName()}|${bu.getMajorVersion()}|`;
  const p1 = (prefix + pname).toLowerCase();
  const p2 = `${prefix}*`.toLowerCase();
  let bRet = false;
  bRet = exclList.includes(p1) || exclList.includes(p2);
  return bRet;
}

/**
 *Checks if the passed window is in iframe
 *
 * @returns {Array<number>} - Width & Height of the screen
 */
export function getViewportDimensions(): [number, number] {
  const win: window = window.top;
  const docEl: HTMLElement = win.document.documentElement;
  let w: number = 0;
  let h: number = 0;
  // Compliant browser supports innerwidth
  if (!isUndefined(win.innerWidth)) {
    w = win.innerWidth;
    h = win.innerHeight;
  } else if (isDOMElement(docEl)) {
    // older browsers
    w = docEl.clientWidth;
    h = docEl.clientHeight;
  }

  return [w, h];
}

/**
 * Check and returns how much given element is
 * visible in viewport vertically
 *
 * @param {document} d - Document object
 * @param {string} divId - Element ID to check
 * @returns {number} - Percentage in viewport
 */
function percentageInVerticalViewPort(
  d: Document = window.document,
  divId: string = ''
): number {
  const ele = d.getElementById(divId);
  if (!isDOMElement(ele)) {
    return 0;
  }
  // $FlowFixMe
  const rect = ele.getBoundingClientRect();

  let percentInVerticalViewport = 100;
  const [, windowHeight] = getViewportDimensions();

  // When the ad is completely visibile within the both window top and bottom bounds OR when it crosses both top and bottom window bounds
  if (
    (rect.top >= 0 && rect.bottom <= windowHeight) ||
    (rect.top < 0 && rect.bottom > windowHeight)
  ) {
    percentInVerticalViewport = 100;
  } else if (rect.bottom > 0 && rect.top < 0) {
    // When the ad is partly visible at the top edge
    percentInVerticalViewport = (rect.bottom / rect.height) * 100;
  } else if (rect.top < windowHeight && rect.bottom > windowHeight) {
    // When the ad is partly visible at the bottom edge
    percentInVerticalViewport = ((windowHeight - rect.top) / rect.height) * 100;
  } else {
    // ad is completely not visible
    percentInVerticalViewport = 0;
  }
  return percentInVerticalViewport;
}

/**
 * Check and returns how much given element is
 * visible in viewport horizontally
 *
 * @param {document} d - Document object
 * @param {string} divId - Element ID to check
 * @returns {number} - Percentage in viewport
 */
function percentageInHorizontalViewPort(
  d: Document = window.document,
  divId: string = ''
): number {
  const ele = d.getElementById(divId);
  if (!isDOMElement(ele)) {
    return 0;
  }
  // $FlowFixMe
  const rect = ele.getBoundingClientRect();

  let percentInHorizontalViewport = 100;
  const [windowWidth] = getViewportDimensions();

  // When the ad is completely visibile within the both window left and right bounds OR when it crosses both left and right window bounds
  if (
    (rect.left >= 0 && rect.right <= windowWidth) ||
    (rect.left < 0 && rect.bottom > windowWidth)
  ) {
    percentInHorizontalViewport = 100;
  } else if (rect.right > 0 && rect.left < 0) {
    // When the ad position is partly visiile at the left edge
    percentInHorizontalViewport = (rect.right / rect.width) * 100;
  } else if (rect.left < windowWidth && rect.right > windowWidth) {
    // When the ad position is partly visiile at the right edge
    percentInHorizontalViewport =
      ((windowWidth - rect.left) / rect.width) * 100;
  } else {
    // ad is completely not visible
    percentInHorizontalViewport = 0;
  }
  return percentInHorizontalViewport;
}

/**
 * Returns the percentage of the element in the viewport
 * The element or its parent should not have any CSS property such as dispaly:none to make it invisible to user
 *
 * @param {Document} d - document object
 * @param {string} divId - Div id of the element
 * @returns {number} - Percentage of div in the viewport
 */
export function percentageInViewPort(
  d: Document = window.document,
  divId: string = ''
): number {
  if (!divId.length) {
    return 0;
  }

  const ele = d.getElementById(divId);
  if (!isDOMElement(ele)) {
    return 0;
  }

  // If getBoundingClientRect is not supported
  // $FlowFixMe
  if (!isFunction(ele.getBoundingClientRect)) {
    return 100;
  }

  return Math.min(
    percentageInVerticalViewPort(d, divId),
    percentageInHorizontalViewPort(d, divId)
  );
}
/**
 * Check browser suppport for native JSON processing
 *
 * @param {window} w - Window object
 * @returns {boolean} - True/False
 */
export function isJSONSupported(w: window = window) {
  return typeof w.JSON === 'object' && isFunction(w.JSON.stringify);
}

/**
 * Check browser suppport for finding effective type of connection
 * and returns the type or returns empty string
 *
 * @returns {string} - {'slow-2g', '2g', '3g', or '4g'}'
 */
export function getEffectiveType() {
  // $FlowFixMe
  return navigator.connection && navigator.connection.effectiveType
    ? navigator.connection.effectiveType || ''
    : '';
}

/**
 * Get effective type and decide if it is dialup or not,
 * if the effective type is not available return the value
 * passed in parameters
 *
 * @param {boolean} d - dialup value from publisher website
 * @returns {boolean} - True/False
 */
export function getEvaluatedDialup(d: boolean) {
  const eType = getEffectiveType();
  if (eType !== '') {
    return eType === 'slow-2g' || eType === '2g';
  }
  return d;
}

/**
 * Returns true if the page is https or
 * Returns false if the page is http
 *
 * @returns {boolean} - true/false
 */
export function isSecure() {
  const loc: Location = window.location;
  return loc.protocol === 'https:';
}

/**
 * Returns true if mobile is supported or
 * Returns false if mobile is not supported
 *
 * @returns {boolean} - true/false
 */
export function isMobile() {
  return BrowserUtil.getUserAgent().includes('Mobi');
}

/**
 * Returns true for no tracking or
 * Returns false for unrestricted tracking
 *
 * @returns {boolean} - true/false
 */
export function isDNT() {
  const dnt: string | number =
    // $FlowFixMe
    navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack || '';
  return dnt === 1 || dnt === '1' || dnt === 'yes';
}
/**
 * Check whether DOM element support given feature or not
 *
 * @typedef {object} tokenList
 * @param {DOMTokenList} tokenList - List of features supported by DOM
 * @param {string} token - Feature to check
 * @returns {boolean} - Feature supported?
 */
function DOMTokenListSupports(tokenList: DOMTokenList, token: string): boolean {
  try {
    // $FlowFixMe
    return tokenList && tokenList.supports && tokenList.supports(token);
  } catch (error) {
    /**/
  }
  return false;
}

/**
 * checks if link preload is supported in the browser
 *
 * @param {Document} d - Document DOM Node
 * @returns {boolean} - True/False
 */
function isLinkPreloadSupported(d: Document = document): boolean {
  const el = d.createElement('link');
  if (!el) {
    return false;
  }
  // $FlowFixMe
  return DOMTokenListSupports(el.relList, 'preload');
}

// don't repeat the check again and again to avoid creating link tags unnecessarily
const isPreloadSupported = isLinkPreloadSupported();

/**
 * Generic function to preload any resources on supported browser
 *
 * @param {Document} d - document object
 * @param {string} src - url of the resource to be preloaded
 * @param {string} as - type
 * @returns {undefined}
 */
export function preloadResource(
  d: Document = document,
  src: string = '',
  as: string = ''
) {
  if (!d || !src.length || !as.length || !isPreloadSupported) {
    return;
  }

  // link preload supported
  preload(src, d, as);
}
/**
 * attach event to given element
 *
 * @param {Element} elem - DOM element
 * @param {string} type - event type
 * @param {function()} cB - event callback function
 * @returns {undefined}
 */
export function addEvent(elem: HTMLElement, type: string, cB: cbFunc) {
  if (elem && elem.addEventListener) {
    elem.addEventListener(type, cB, false);
  } else if (elem && elem.attachEvent) {
    elem.attachEvent(`on${type}`, cB);
  }
}

/**
 * remove event attached to an element
 *
 * @param {Element} elem - DOM element
 * @param {string} type - event type
 * @param {function()} cB - event callback function
 * @returns {undefined}
 */
export function removeEvent(elem: HTMLElement, type: string, cB: cbFunc) {
  if (elem && elem.removeEventListener) {
    elem.removeEventListener(type, cB, false);
  } else if (elem && elem.detachEvent) {
    elem.detachEvent(`on${type}`, cB);
  }
}

/**
 * Send tracking pixel request using navigator.sendBeacon
 * in modern browsers
 *
 * @param {string} url beacon request url
 * @returns {undefined}
 */
export function sendBeacon(url: string) {
  try {
    return navigator && navigator.sendBeacon && navigator.sendBeacon(url);
  } catch (error) {
    logError(`sendBeacon failed with error`);
  }
  return false;
}

/**
 *Checks if the passed window is in iframe
 *
 * @param {window} [w] - window object
 * @returns {boolean} - If the passed window is in an iframe
 */
export function inIframe(w?: window = window): boolean {
  return w.self && w.top && w.self !== w.top;
}

/**
 *
 * @param {string} str Argument
 * @returns {string} with Title case
 */
function camelCaseToTitleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const vendorPrefixes = ['Webkit', 'webkit', 'Moz', 'moz', 'ms', 'O', 'o'];

/**
 * Get supported vendor prefix for the given property
 *
 * @param {Document} doc Document object
 * @param {string} prop Property to check
 * @returns {string} Vendor prefix if applicable
 */
export function getSupportedVendorPrefix(doc: Document, prop: string) {
  if (!isString(prop) || prop.length < 1) {
    return '';
  }
  // Check if property is supported
  // $FlowFixMe
  if (!isUndefined(doc[prop])) {
    return '';
  }

  // get title case of property hidden => Hidden
  const modifiedProp = camelCaseToTitleCase(prop);
  const found = vendorPrefixes.findIndex(
    // $FlowFixMe
    prefix => !isUndefined(doc[`${prefix}${modifiedProp}`])
  );
  if (found !== -1) {
    return vendorPrefixes[found];
  }
  return '';
}
/**
 *
 * utility to get vendor prefixed supported prob
 *
 * @param {Document} doc Window document
 * @param {string} prop Property to get with vendor prefix
 * @returns {string} Vendor prefix-ed & supported property
 */
export function getVendorJsPropertyName(doc: Document, prop: string): string {
  if (!isString(prop) || prop.length < 1) {
    return '';
  }

  return `${getSupportedVendorPrefix(doc, prop)}${prop}`;
}
